// PWA — 电源面板的纯展示逻辑（可测，Req 6.1/6.3）
//
// 把「PowerState + 是否就绪 + 启动流程是否进行中」映射为面板所需的展示模型：
// 中文标签、是否可点「启动」、是否处于忙碌（轮询 / 冷加载）态、以及冷加载进度提示。
// 纯函数、无副作用，便于单元测试；组件与 hook 复用之。

import { decideStart, type PowerState } from "@comfy/shared";

/** 电源状态的中文标签（与 App 头部 pill 语义一致）。 */
export const POWER_LABELS: Record<PowerState, string> = {
  running: "运行中",
  stopped: "已停机",
  starting: "启动中",
  stopping: "停止中",
  unavailable: "状态未知",
};

/** 冷加载预计耗时（秒）：实测约 40s（design.md「Cold_Load」）。 */
export const COLD_LOAD_ESTIMATE_SEC = 40;

/** 面板展示模型。 */
export interface PowerView {
  /** 主状态中文标签。 */
  label: string;
  /** 是否应展示并允许点击「启动」按钮（仅 stopped 态且未在启动流程中）。 */
  canStart: boolean;
  /** 是否应展示「关机」按钮（实例已开机：running / starting）。关机受管理员密码保护。 */
  canStop: boolean;
  /** 是否处于忙碌态（启动流程 / 冷加载中，UI 应显示进度）。 */
  busy: boolean;
  /** 更细的展示相位，驱动进度文案。 */
  phase: PowerPhase;
}

/** 面板展示相位。 */
export type PowerPhase =
  | "running-ready" // running 且 ComfyUI 就绪 → 可用
  | "cold-loading" // running 但未就绪 → 冷加载中（约 40s）
  | "starting" // pending / 已触发 start，轮询至 running
  | "stopped" // 已停机，可启动
  | "stopping" // 停止中
  | "unavailable"; // 非可用态

/**
 * 依据当前状态推导展示相位。
 * @param state 当前 PowerState。
 * @param comfyReady ComfyUI 是否就绪（仅 running 时有意义）。
 * @param startingFlow 是否处于「用户触发启动」的进行中流程。
 */
export function derivePhase(
  state: PowerState,
  comfyReady: boolean,
  startingFlow: boolean,
): PowerPhase {
  if (state === "running") {
    return comfyReady ? "running-ready" : "cold-loading";
  }
  if (state === "starting") return "starting";
  if (state === "stopping") return "stopping";
  if (state === "stopped") {
    // 已触发启动但 EC2 尚未离开 stopped 态的短暂窗口，也视为 starting。
    return startingFlow ? "starting" : "stopped";
  }
  return "unavailable";
}

/**
 * 计算面板展示模型。`canStart` 复用 shared 的 {@link decideStart}：
 * 仅当决策需要调用 StartInstances（即 stopped 态）且当前未在启动流程中时可点。
 */
export function derivePowerView(
  state: PowerState,
  comfyReady: boolean,
  startingFlow: boolean,
): PowerView {
  const phase = derivePhase(state, comfyReady, startingFlow);
  const canStart = decideStart(state).callStartInstances && !startingFlow;
  // 关机按钮：实例已开机（running / starting）时可见；stopped/stopping/unavailable 不显示。
  const canStop = state === "running" || state === "starting";
  const busy =
    startingFlow || phase === "starting" || phase === "cold-loading";
  return { label: POWER_LABELS[state], canStart, canStop, busy, phase };
}

/**
 * 冷加载 / 启动进度文案：显示已耗时与预计等待。
 * @param phase 展示相位。
 * @param elapsedSec 启动流程已耗时（秒）。
 * @param estimateSec 预计冷加载总耗时（秒）。
 */
export function progressText(
  phase: PowerPhase,
  elapsedSec: number,
  estimateSec: number = COLD_LOAD_ESTIMATE_SEC,
): string | null {
  if (phase === "starting") {
    return `正在启动实例…已等待 ${elapsedSec}s`;
  }
  if (phase === "cold-loading") {
    const remain = Math.max(0, estimateSec - elapsedSec);
    return `模型加载中（约 ${estimateSec}s）…已等待 ${elapsedSec}s，预计还需 ${remain}s`;
  }
  return null;
}
